import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

interface Stats {
  parents: number;
  kids: number;
  rooms: number;
  openRooms: number;
  messages: number;
  openTickets: number;
}
interface AdminUser {
  id: string;
  role: string;
  email: string;
  displayName: string;
  status: 'active' | 'suspended';
  lastLoginAt: string | null;
}

export function AdminConsole() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);

  async function loadUsers() {
    setUsers((await api<{ users: AdminUser[] }>('/api/admin/users')).users);
  }
  useEffect(() => {
    api<Stats>('/api/admin/stats').then(setStats).catch(() => undefined);
    loadUsers().catch(() => undefined);
  }, []);

  async function setStatus(u: AdminUser, status: 'active' | 'suspended') {
    await api(`/api/admin/users/${u.id}/status`, { method: 'POST', body: JSON.stringify({ status }) });
    await loadUsers();
  }

  return (
    <div style={{ padding: 'var(--space-8)', maxWidth: 'var(--container-max, 1200px)', margin: '0 auto' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <span className="kz-eyebrow">Admin console</span>
          <h1 style={{ marginTop: 'var(--space-1)' }}>KidzIM operations</h1>
        </div>
        <div className="kz-row">
          <Button variant="secondary" onClick={() => nav('/app')}>Parent view</Button>
          <Button variant="ghost" onClick={async () => { await logout(); nav('/'); }}>Sign out</Button>
        </div>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}>
          <Stat label="Parents" value={stats.parents} />
          <Stat label="Kids" value={stats.kids} />
          <Stat label="Rooms" value={stats.rooms} />
          <Stat label="Open now" value={stats.openRooms} />
          <Stat label="Messages" value={stats.messages} />
          <Stat label="Open tickets" value={stats.openTickets} />
        </div>
      )}

      <h2 style={{ marginBottom: 'var(--space-4)' }}>Users</h2>
      <div className="kz-stack">
        {users.map((u) => (
          <Card key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="kz-row">
                <strong>{u.displayName}</strong>
                <Badge tone={u.role === 'admin' ? 'brand' : 'neutral'}>{u.role}</Badge>
                {u.status === 'suspended' && <Badge tone="flag">suspended</Badge>}
              </div>
              <p className="kz-muted" style={{ font: 'var(--type-small)' }}>{u.email}</p>
            </div>
            {u.role !== 'admin' && (
              u.status === 'active' ? (
                <Button variant="danger" size="sm" onClick={() => setStatus(u, 'suspended')}>Suspend</Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setStatus(u, 'active')}>Reactivate</Button>
              )
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card style={{ textAlign: 'center' }}>
      <p style={{ font: 'var(--type-display)', color: 'var(--blue-600)' }}>{value}</p>
      <p className="kz-muted" style={{ font: 'var(--type-small)' }}>{label}</p>
    </Card>
  );
}
